import React, { useEffect, useRef } from "react"
import Layout from "@layouts/layout"
import { graphql } from "gatsby"
import {
  Badge,
  Block,
  Carousel,
  Container,
  Cards,
  Card,
  Banner
} from "@components"
import ModuleArea from "../helpers/moduleArea"
import { promoChecker, filterByLocale } from "../helpers/miscHelpers"
import { HelmetDatoCms } from "gatsby-source-datocms"
import { InView } from "react-intersection-observer"

const siteLanguage = process.env.GATSBY_LANGUAGE

const birdeyeConfig = {
  "en-AU": {
    scriptSrc:
      "https://getbirdeye.com.au/embed/v7/176420374302641/10/987654321301015183",
    widgetId: "bf-revz-widget-987654321301015183"
  },
  "en-NZ": {
    scriptSrc:
      "https://getbirdeye.com.au/embed/v7/176420444155440/10/987654321301015183",
    widgetId: "bf-revz-widget-987654321301015183"
  }
}

const BirdeyeWidget = () => {
  const config = birdeyeConfig[siteLanguage] || birdeyeConfig["en-AU"]
  const widgetRef = useRef(null)

  useEffect(() => {
    // Use a small timeout to ensure DOM is ready and widget container exists
    let retryCount = 0
    const maxRetries = 10

    const initBirdeye = () => {
      // Ensure widget container exists (using ref or getElementById)
      const widgetContainer =
        widgetRef.current || document.getElementById(config.widgetId)

      if (!widgetContainer && retryCount < maxRetries) {
        // Container not ready yet, try again
        retryCount++
        setTimeout(initBirdeye, 50)
        return
      }

      if (!widgetContainer) {
        // Container still not found after retries, skip initialization
        return
      }

      // Clear the widget container to allow fresh initialization
      widgetContainer.innerHTML = ""

      // Remove existing script if present to ensure clean re-initialization
      const existingScript = document.querySelector(
        `script[src='${config.scriptSrc}']`
      )
      if (existingScript) {
        existingScript.remove()
      }

      // Add the script fresh to trigger initialization
      const script = document.createElement("script")
      script.type = "text/javascript"
      script.src = config.scriptSrc
      script.async = true
      document.body.appendChild(script)
    }

    // Small delay to ensure DOM is ready, especially on client-side navigation
    const timeoutId = setTimeout(initBirdeye, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [config.scriptSrc, config.widgetId])

  return <div ref={widgetRef} id={config.widgetId} />
}

const IndexPage = ({
  data: {
    datoCmsSite,
    home: {
      homepageCarousel,
      showPathways,
      pathways,
      productCarousel,
      content,
      seoMetaTags,
      heading
    },
    globalConfig: { defaultPathways, trustMarksNew },
    promotions
  }
}) => {
  const allPathways = [...pathways, ...defaultPathways].slice(0, 3)

  const promos = filterByLocale(promotions.nodes)

  const promoBanners = promos.map(promo => {
    if (promo.disableHomepageBanner) return null
    if (promoChecker(promo.startDate, promo.endDate, promo.locales))
      return {
        id: promo.id,
        image: promo.banner,
        mobileImage: promo.mobileBanner,
        link: promo.pageLink
          ? {
              link: {
                path: promo.pageLink.path
              }
            }
          : null
      }
    else return null
  })
  const filteredPromoBanners = promoBanners.filter(Boolean)
  // The homepage carousel can be left empty in Dato so only promo banners show
  const carouselItems = [...filteredPromoBanners, ...(homepageCarousel || [])]
  return (
    <Layout>
      <HelmetDatoCms defer={false} seo={seoMetaTags}>
        <meta
          property="og:site_name"
          content={datoCmsSite.globalSeo.siteName}
        />
      </HelmetDatoCms>
      {carouselItems.length > 0 && (
        <Block guttersReverse padding="both-small-no-top">
          <Container>
            <Carousel
              moduleStyle="hero"
              items={carouselItems}
              loading="eager"
            />
          </Container>
        </Block>
      )}
      {showPathways && (
        <Block padding="both" gutters>
          <Cards>
            {allPathways.map(pathway => {
              const { link, displayText, url } = pathway.link
              return (
                <Card
                  key={`pathway-${pathway.id}`}
                  {...pathway}
                  link={{
                    path: link && link.path,
                    title: displayText,
                    url: url && url
                  }}
                  pathway
                  elevated
                  truncate={3}
                />
              )
            })}
          </Cards>
        </Block>
      )}
      <Block padding="double" heading={heading} isH1>
        <Cards
          orientation="horizontal"
          columns={trustMarksNew.length === 5 ? "5" : "4"}
          size="large"
          modifier={trustMarksNew.length === 5 ? "trustmarks-5" : undefined}
        >
          {trustMarksNew.map(({ darkIcon: { url }, id, heading, link }) => {
            const badge = (
              <Badge
                image={url}
                alt={heading}
                key={`trustmark-${id}`}
                heading={heading}
                moduleStyle="trustmark"
              />
            )
            if (link) {
              let to = "/"
              if (link.url) {
                to = link.url
              } else if (link.link && link.link.path) {
                to = link.link.path
              }
              if (link.locationHash) {
                to += link.locationHash
              }
              return (
                <a
                  href={to}
                  aria-label={link.displayText || heading}
                  key={`trustmark-${id}`}
                  target={link.newWindow ? "_blank" : undefined}
                  rel={link.newWindow ? "noopener noreferrer" : undefined}
                >
                  {badge}
                </a>
              )
            }
            return badge
          })}
        </Cards>
      </Block>
      <Block gutters>
        <Container>
          <Block gutters padding="both">
            <InView>
              {({ inView, ref, entry }) => (
                <div ref={ref}>
                  <BirdeyeWidget />
                </div>
              )}
            </InView>
          </Block>
        </Container>
      </Block>

      <Block padding="double-top-single-bottom">
        <Container>
          <Carousel
            heading="Featured Products"
            moduleStyle="products"
            items={productCarousel}
            showThumbNavigation
          />
        </Container>
      </Block>
      {content && (
        <Block>
          <ModuleArea modules={content} />
        </Block>
      )}
    </Layout>
  )
}

export default IndexPage

export const query = graphql`
  query IndexQuery($language: String!) {
    datoCmsSite(locale: $language) {
      name
      globalSeo {
        siteName
      }
    }
    home: datoCmsHome(locale: $language) {
      showPathways
      heading
      ...HomeContentModuleArea
      pathways {
        ...Card
      }
      ...CarouselProductSlide
      ...CarouselHomeImageSlide

      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }

    globalConfig: datoCmsGlobal(locales: { eq: $language }) {
      contactPage {
        path
      }
      defaultPathways {
        ...Card
      }
      trustMarksNew {
        id
        heading
        darkIcon {
          url
        }
        lightIcon {
          url
        }
        link {
          ...Link
        }
      }
    }
    promotions: allDatoCmsPromotion(
      filter: { locales: { eq: $language } }
      sort: { order: ASC, fields: position }
    ) {
      nodes {
        id
        name
        startDate
        endDate
        locales
        disableHomepageBanner
        banner {
          alt
          gatsbyImageData(
            layout: FULL_WIDTH
            imgixParams: {
              h: "1100"
              fit: "crop"
              w: "2340"
              q: 70
              auto: "format"
            }
          )
        }
        mobileBanner {
          alt
          gatsbyImageData(
            layout: FULL_WIDTH
            imgixParams: {
              h: "914"
              fit: "crop"
              w: "750"
              q: 70
              auto: "format"
            }
          )
          alt
        }
        pageLink {
          ... on DatoCmsPage {
            id
            path
          }
          ... on DatoCmsLandingPage {
            id
            path
          }
          ... on DatoCmsCollection {
            id
            path
          }
        }
      }
    }
  }
`
